// client/src/reducers/adminReducerForProducts.js

import * as A from "../constants/adminProductConstants";

/* -------------------------------------------------------------------------- */
/*                                  Defaults                                  */
/* -------------------------------------------------------------------------- */

const EMPTY_FACETS = Object.freeze({
  colors: [],
  materials: [],
  sizes: [],
  patterns: [],
  brands: [],
  unitTypes: [],
  finishes: [],
  compatibilities: [],
  genders: [],
  eras: [],
  tags: [],
  groups: [],
});

const DEFAULT_PAGINATION = Object.freeze({
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 1,
  hasNextPage: false,
  hasPrevPage: false,
});

const initialListState = {
  loading: false,
  products: [],
  productsByShop: {},
  facets: { ...EMPTY_FACETS },
  page: 1,
  pages: 1,
  count: 0,
  pagination: { ...DEFAULT_PAGINATION },
  filters: {},
  error: null,
  errorMeta: null,
};

const initialDetailsState = {
  loading: false,
  product: null,
  error: null,
  errorMeta: null,
};

const initialMutationState = {
  loading: false,
  success: false,
  product: null,
  error: null,
  errorMeta: null,
};

const initialDeleteState = {
  loading: false,
  success: false,
  changed: false,
  message: "",
  productId: null,
  mode: null,
  status: null,
  isPublished: undefined,
  isDeleted: undefined,
  product: null,
  error: null,
  errorMeta: null,
};

const initialStatusState = {
  loading: false,
  success: false,
  changed: false,
  message: "",
  productId: null,
  mode: null,
  status: null,
  isPublished: undefined,
  isDeleted: undefined,
  product: null,
  error: null,
  errorMeta: null,
};

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

function safeText(value = "") {
  return String(value ?? "").trim();
}

function normalizeLower(value = "") {
  return safeText(value).toLowerCase();
}

function toFiniteNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function toPositiveInteger(value, fallback = 1) {
  const number = Math.floor(Number(value));
  return Number.isFinite(number) && number > 0 ? number : fallback;
}

function normalizeErrorMeta(payload) {
  if (!payload) return null;

  if (typeof payload === "string") {
    return {
      message: payload,
      code: "",
      status: 0,
      requestId: "",
      issues: undefined,
    };
  }

  return {
    message: safeText(payload.message || "Something went wrong."),
    code: safeText(payload.code),
    status: toFiniteNumber(payload.status, 0),
    requestId: safeText(payload.requestId),
    issues: Array.isArray(payload.issues) ? payload.issues : undefined,
  };
}

function normalizeErrorMessage(payload) {
  return normalizeErrorMeta(payload)?.message || null;
}

function normalizeFacetArray(value) {
  if (!Array.isArray(value)) return [];

  return [...new Set(value.map(safeText).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b),
  );
}

function normalizeFacets(facets = {}) {
  return {
    colors: normalizeFacetArray(facets.colors),
    materials: normalizeFacetArray(facets.materials),
    sizes: normalizeFacetArray(facets.sizes),
    patterns: normalizeFacetArray(facets.patterns),
    brands: normalizeFacetArray(facets.brands),
    unitTypes: normalizeFacetArray(facets.unitTypes),
    finishes: normalizeFacetArray(facets.finishes),
    compatibilities: normalizeFacetArray(facets.compatibilities),
    genders: normalizeFacetArray(facets.genders),
    eras: normalizeFacetArray(facets.eras),
    tags: normalizeFacetArray(facets.tags),
    groups: normalizeFacetArray(facets.groups),
  };
}

function extractProducts(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.products)) return payload.products;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;

  return [];
}

function normalizeProductsByShop(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};

  return Object.entries(value).reduce((acc, [shopKey, bucket]) => {
    if (Array.isArray(bucket)) {
      acc[shopKey] = {
        all: bucket,
        simple: bucket.filter((product) => !product?.isVariant),
        variant: bucket.filter((product) => product?.isVariant),
      };

      return acc;
    }

    const safeBucket = bucket && typeof bucket === "object" ? bucket : {};

    acc[shopKey] = {
      all: Array.isArray(safeBucket.all) ? safeBucket.all : [],
      simple: Array.isArray(safeBucket.simple) ? safeBucket.simple : [],
      variant: Array.isArray(safeBucket.variant) ? safeBucket.variant : [],
    };

    return acc;
  }, {});
}

function normalizePagination(payload = {}, products = []) {
  const pagination = payload?.pagination || {};

  const page = toPositiveInteger(pagination.page ?? payload.page, 1);
  const limit = toPositiveInteger(pagination.limit ?? payload.limit, 20);

  const total = Math.max(
    0,
    toFiniteNumber(
      pagination.total ??
        pagination.count ??
        payload.total ??
        payload.count ??
        payload.totalProducts ??
        products.length,
      products.length,
    ),
  );

  const totalPages = Math.max(
    1,
    toPositiveInteger(
      pagination.totalPages ?? payload.totalPages ?? payload.pages,
      Math.ceil(total / limit) || 1,
    ),
  );

  return {
    page,
    limit,
    total,
    totalPages,
    hasNextPage:
      typeof pagination.hasNextPage === "boolean"
        ? pagination.hasNextPage
        : page < totalPages,
    hasPrevPage:
      typeof pagination.hasPrevPage === "boolean"
        ? pagination.hasPrevPage
        : page > 1,
  };
}

function normalizeListSuccess(payload = {}) {
  const products = extractProducts(payload).filter(Boolean);
  const pagination = normalizePagination(payload, products);

  return {
    loading: false,
    products,
    productsByShop: normalizeProductsByShop(payload?.productsByShop),
    facets: normalizeFacets(payload?.facets || {}),
    page: pagination.page,
    pages: pagination.totalPages,
    count: pagination.total,
    pagination,
    filters:
      payload?.filters && typeof payload.filters === "object"
        ? payload.filters
        : {},
    error: null,
    errorMeta: null,
  };
}

function getProductPayload(payload) {
  return payload?.product || payload || null;
}

function getLifecycleMessage(payload = {}, fallback = "Product updated") {
  const explicit = safeText(payload.message);
  if (explicit) return explicit;

  const status = normalizeLower(payload.status);

  if (status === "published") return "Product published";
  if (status === "draft") return "Product moved to draft";
  if (status === "deleted") return "Product deleted";

  return fallback;
}

function getLifecycleStatus(payload = {}, meta = {}) {
  return normalizeLower(payload.status || meta.status) || null;
}

function getLifecycleProductId(payload = {}, meta = {}) {
  return payload.productId || payload.id || meta.id || null;
}

function getLifecycleMode(payload = {}, meta = {}, fallback = "status") {
  return normalizeLower(payload.mode || meta.mode || fallback);
}

/* -------------------------------------------------------------------------- */
/*                                   Reducers                                 */
/* -------------------------------------------------------------------------- */

export function adminProductListReducer(state = initialListState, action) {
  switch (action.type) {
    case A.ADMIN_PRODUCT_LIST_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        errorMeta: null,
      };

    case A.ADMIN_PRODUCT_LIST_SUCCESS:
      return normalizeListSuccess(action.payload);

    case A.ADMIN_PRODUCT_LIST_FAIL:
      return {
        ...state,
        loading: false,
        error: normalizeErrorMessage(action.payload),
        errorMeta: normalizeErrorMeta(action.payload),
      };

    case A.ADMIN_PRODUCT_LIST_RESET:
      return initialListState;

    default:
      return state;
  }
}

export function adminProductDetailsReducer(
  state = initialDetailsState,
  action,
) {
  switch (action.type) {
    case A.ADMIN_PRODUCT_DETAILS_REQUEST:
      return {
        ...initialDetailsState,
        loading: true,
      };

    case A.ADMIN_PRODUCT_DETAILS_SUCCESS:
      return {
        loading: false,
        product: getProductPayload(action.payload),
        error: null,
        errorMeta: null,
      };

    case A.ADMIN_PRODUCT_DETAILS_FAIL:
      return {
        loading: false,
        product: null,
        error: normalizeErrorMessage(action.payload),
        errorMeta: normalizeErrorMeta(action.payload),
      };

    case A.ADMIN_PRODUCT_DETAILS_RESET:
      return initialDetailsState;

    default:
      return state;
  }
}

export function adminProductCreateReducer(
  state = initialMutationState,
  action,
) {
  switch (action.type) {
    case A.ADMIN_PRODUCT_CREATE_REQUEST:
      return {
        ...initialMutationState,
        loading: true,
      };

    case A.ADMIN_PRODUCT_CREATE_SUCCESS:
      return {
        loading: false,
        success: true,
        product: getProductPayload(action.payload),
        error: null,
        errorMeta: null,
      };

    case A.ADMIN_PRODUCT_CREATE_FAIL:
      return {
        loading: false,
        success: false,
        product: null,
        error: normalizeErrorMessage(action.payload),
        errorMeta: normalizeErrorMeta(action.payload),
      };

    case A.ADMIN_PRODUCT_CREATE_RESET:
      return initialMutationState;

    default:
      return state;
  }
}

export function adminProductUpdateReducer(
  state = initialMutationState,
  action,
) {
  switch (action.type) {
    case A.ADMIN_PRODUCT_UPDATE_REQUEST:
      return {
        ...initialMutationState,
        loading: true,
      };

    case A.ADMIN_PRODUCT_UPDATE_SUCCESS:
      return {
        loading: false,
        success: true,
        product: getProductPayload(action.payload),
        error: null,
        errorMeta: null,
      };

    case A.ADMIN_PRODUCT_UPDATE_FAIL:
      return {
        loading: false,
        success: false,
        product: null,
        error: normalizeErrorMessage(action.payload),
        errorMeta: normalizeErrorMeta(action.payload),
      };

    case A.ADMIN_PRODUCT_UPDATE_RESET:
      return initialMutationState;

    default:
      return state;
  }
}

export function adminProductDeleteReducer(state = initialDeleteState, action) {
  switch (action.type) {
    case A.ADMIN_PRODUCT_DELETE_REQUEST:
      return {
        ...initialDeleteState,
        loading: true,
        mode: getLifecycleMode(action.payload, action.meta, "delete"),
        productId: action.meta?.id || null,
      };

    case A.ADMIN_PRODUCT_DELETE_SUCCESS: {
      const status =
        getLifecycleStatus(action.payload, action.meta) || "deleted";

      return {
        loading: false,
        success: true,
        changed: Boolean(action.payload?.changed ?? true),
        message: getLifecycleMessage(action.payload, "Product deleted"),
        productId: getLifecycleProductId(action.payload, action.meta),
        mode: getLifecycleMode(action.payload, action.meta, "delete"),
        status,
        isPublished:
          typeof action.payload?.isPublished === "boolean"
            ? action.payload.isPublished
            : false,
        isDeleted:
          typeof action.payload?.isDeleted === "boolean"
            ? action.payload.isDeleted
            : true,
        product: action.payload?.product || null,
        error: null,
        errorMeta: null,
      };
    }

    case A.ADMIN_PRODUCT_DELETE_FAIL:
      return {
        ...initialDeleteState,
        mode: getLifecycleMode(action.payload, action.meta, "delete"),
        productId: action.meta?.id || null,
        error: normalizeErrorMessage(action.payload),
        errorMeta: normalizeErrorMeta(action.payload),
      };

    case A.ADMIN_PRODUCT_DELETE_RESET:
      return initialDeleteState;

    default:
      return state;
  }
}

export function adminProductStatusReducer(state = initialStatusState, action) {
  switch (action.type) {
    case A.ADMIN_PRODUCT_SET_STATUS_REQUEST:
      return {
        ...initialStatusState,
        loading: true,
        mode: getLifecycleMode(action.payload, action.meta, "status"),
        status: getLifecycleStatus(action.payload, action.meta),
        productId: action.meta?.id || null,
      };

    case A.ADMIN_PRODUCT_SET_STATUS_SUCCESS: {
      const status = getLifecycleStatus(action.payload, action.meta);

      return {
        loading: false,
        success: true,
        changed: Boolean(action.payload?.changed ?? true),
        message: getLifecycleMessage(action.payload, "Product status updated"),
        productId: getLifecycleProductId(action.payload, action.meta),
        mode: getLifecycleMode(action.payload, action.meta, "status"),
        status,
        isPublished:
          typeof action.payload?.isPublished === "boolean"
            ? action.payload.isPublished
            : status === "published",
        isDeleted:
          typeof action.payload?.isDeleted === "boolean"
            ? action.payload.isDeleted
            : status === "deleted",
        product: action.payload?.product || null,
        error: null,
        errorMeta: null,
      };
    }

    case A.ADMIN_PRODUCT_SET_STATUS_FAIL:
      return {
        ...initialStatusState,
        mode: getLifecycleMode(action.payload, action.meta, "status"),
        status: getLifecycleStatus(action.payload, action.meta),
        productId: action.meta?.id || null,
        error: normalizeErrorMessage(action.payload),
        errorMeta: normalizeErrorMeta(action.payload),
      };

    case A.ADMIN_PRODUCT_SET_STATUS_RESET:
      return initialStatusState;

    default:
      return state;
  }
}

/* -------------------------------------------------------------------------- */
/*                         Backwards-compatible reducers                       */
/* -------------------------------------------------------------------------- */

export function adminProductUnpublishReducer(
  state = initialStatusState,
  action,
) {
  switch (action.type) {
    case A.ADMIN_PRODUCT_UNPUBLISH_REQUEST:
      return {
        ...initialStatusState,
        loading: true,
        mode: "unpublish",
      };

    case A.ADMIN_PRODUCT_UNPUBLISH_SUCCESS:
      return {
        ...initialStatusState,
        loading: false,
        success: true,
        changed: Boolean(action.payload?.changed ?? true),
        message: getLifecycleMessage(action.payload, "Product moved to draft"),
        productId: getLifecycleProductId(action.payload, action.meta),
        mode: "unpublish",
        status: "draft",
        isPublished: false,
        isDeleted: false,
        product: action.payload?.product || null,
      };

    case A.ADMIN_PRODUCT_UNPUBLISH_FAIL:
      return {
        ...initialStatusState,
        mode: "unpublish",
        error: normalizeErrorMessage(action.payload),
        errorMeta: normalizeErrorMeta(action.payload),
      };

    case A.ADMIN_PRODUCT_UNPUBLISH_RESET:
      return initialStatusState;

    default:
      return state;
  }
}

export function adminProductRestoreReducer(state = initialStatusState, action) {
  switch (action.type) {
    case A.ADMIN_PRODUCT_RESTORE_REQUEST:
      return {
        ...initialStatusState,
        loading: true,
        mode: "restore",
      };

    case A.ADMIN_PRODUCT_RESTORE_SUCCESS:
      return {
        ...initialStatusState,
        loading: false,
        success: true,
        changed: Boolean(action.payload?.changed ?? true),
        message: getLifecycleMessage(action.payload, "Product restored"),
        productId: getLifecycleProductId(action.payload, action.meta),
        mode: "restore",
        status: getLifecycleStatus(action.payload, action.meta) || "draft",
        isPublished: Boolean(action.payload?.isPublished),
        isDeleted: Boolean(action.payload?.isDeleted),
        product: action.payload?.product || null,
      };

    case A.ADMIN_PRODUCT_RESTORE_FAIL:
      return {
        ...initialStatusState,
        mode: "restore",
        error: normalizeErrorMessage(action.payload),
        errorMeta: normalizeErrorMeta(action.payload),
      };

    case A.ADMIN_PRODUCT_RESTORE_RESET:
      return initialStatusState;

    default:
      return state;
  }
}

const adminReducerForProducts = {
  adminProductListReducer,
  adminProductDetailsReducer,
  adminProductCreateReducer,
  adminProductUpdateReducer,
  adminProductDeleteReducer,
  adminProductStatusReducer,
  adminProductUnpublishReducer,
  adminProductRestoreReducer,
};

export default adminReducerForProducts;
